import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { InterceptorHttpParams } from '../core/error/http-error-interceptor.service';
import { IProductPermission } from './user-product-permission-item/user-product-permission-item.component';

const resourceBaseUrl = '/api/v1/users';

export interface IAddUserWithProductsParameters {
  firstName: string;
  lastName: string;
  email: string;
  loggedInUserId?: string;
  products: IProductPermission[];
}

export interface IUserState {
  state: 'STAGED';
}

export interface IAddUserWithProductPermissionsResult {
  subId: string;
  role: string;
  error: IErrorResponse;
}

export interface IErrorResponse {
  errorCode: string;
  errorDescription: string;
}

export interface IUpdateProductPermissionsResult extends IProductPermission {
  error: IErrorResponse;
}

export interface IProductUserPermission {
  userId: string;
  role: string;
}

export interface IProductUserPermissionResult extends IProductUserPermission {
  error?: IErrorResponse;
}

export interface IUserInviteUser {
  userId: string;
  email: string;
  displayName: string;
}

export interface IProductUserSubscriptionPermission extends IUserInviteUser {
  permission: string;
}

export interface IExtendedUserAttributes {
  apimPlatformUserId: string;
}

export interface IProfile {
  username: string;
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  idpAccountId: string;
  title?: string;
  locale?: string;
  status: string;
  extendedUserAttributes: IExtendedUserAttributes;
  id: string;
}

export interface IUserInfo {
  user: IProfile;
  entitlements?: IEntitlements;
}

export interface IEntitlements {
  subscriptionMemberships?: ISubscriptionMembershipData[];
}

export interface ISubscriptionMembershipData {
  subscription?: ISubscription;
}

export interface ISubscription {
  subscriptionId?: string;
  companyName?: string;
}

export interface IUserProductPermission {
  startDate: Date | string;
  endDate: Date | string;
  geography?: string;
  permission: string;
  productId: string;
  productType?: string;
  subscriptionId: string;
  updatesAllowed?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class UserDataAccessService {
  constructor(private _http: HttpClient) {}

  private static _permissionMapper(permission: IUserProductPermission): IUserProductPermission {
    // The response actually has string type for start and end dates.
    // We can get away with not declaring another interface with string types because new Date() also takes a date input.
    // IE will not process dates with +0000 offsets so we must replace that with z
    permission.endDate = !!permission.endDate
      ? // Note that IE11 requires that the Z be capital.
        new Date((permission.endDate as string).replace('+0000', 'Z'))
      : permission.endDate;
    permission.startDate = !!permission.startDate
      ? new Date((permission.startDate as string).replace('+0000', 'Z'))
      : permission.startDate;
    return permission;
  }

  public getUsers(): Observable<IUserInviteUser[]> {
    return this._http.get<IUserInviteUser[]>(resourceBaseUrl).pipe(shareReplay(1));
  }

  public getUserProductPermissions(userId: string): Observable<IUserProductPermission[]> {
    const url = `${resourceBaseUrl}/${userId}/products`;
    return this._http.get<IUserProductPermission[]>(url).pipe(
      map((response) => response.map(UserDataAccessService._permissionMapper)),
      shareReplay(1),
    );
  }

  public getProductUsersPermissions(subscriptionId: string): Observable<IProductUserSubscriptionPermission[]> {
    const url = `${resourceBaseUrl}/${subscriptionId}/product-users`;
    return this._http.get<IProductUserSubscriptionPermission[]>(url).pipe(shareReplay(1));
  }

  public updateUserProductPermission(
    userId: string,
    permissions: IProductPermission[],
  ): Observable<IUpdateProductPermissionsResult[]> {
    // TODO - Update the backend API to return a 400, if errors exist while updating
    const url = `${resourceBaseUrl}/${userId}/product-permissions`;
    return this._http.put<IUpdateProductPermissionsResult[]>(url, permissions).pipe(shareReplay(1));
  }

  public getUserInfo(email: string): Observable<IUserInfo> {
    const url = `${resourceBaseUrl}/${email}/info`;
    return this._http.get<IUserInfo>(url, {
      params: new InterceptorHttpParams({ statusCodesToIgnore: [404] }),
    });
  }

  public getUserState(email: string): Observable<IUserState> {
    const url = `${resourceBaseUrl}/${email}/state`;
    return this._http.get<IUserState>(url, {
      params: new InterceptorHttpParams({ statusCodesToIgnore: [404] }),
    });
  }

  public getProductPermissions(): Observable<IUserProductPermission[]> {
    const url = '/api/v1/users/product-permissions';
    return this._http.get<IUserProductPermission[]>(url).pipe(
      map((response) => response.map(UserDataAccessService._permissionMapper)),
      shareReplay(1),
    );
  }

  public addUserWithProductPermissions(
    payLoad: IAddUserWithProductsParameters,
  ): Observable<IAddUserWithProductPermissionsResult[]> {
    return this._http.post<IAddUserWithProductPermissionsResult[]>(resourceBaseUrl, payLoad, {
      params: new InterceptorHttpParams({ statusCodesToIgnore: [400] }),
    });
  }

  public updateProductUsersPermission(
    subscriptionId: string,
    permissions: IProductUserPermission[],
  ): Observable<IProductUserPermissionResult[]> {
    const url = `${resourceBaseUrl}/${subscriptionId}/product-users-permissions`;
    return this._http.put<IProductUserPermissionResult[]>(url, permissions).pipe(shareReplay(1));
  }
}
